import { type PaymentReceiptData } from "@/components/common/PaymentReceipt";
import { getCurrencySymbol, getActiveIntlLocale } from '@/utils/currency';
import { printEscPosBytes, isThermalPrinterConfigured } from '@/hooks/usePrinterConnection';
import { usePrinterStore } from '@/store/printerStore';



/**
 * ESC/POS command builder for thermal printers
 * https://en.wikipedia.org/wiki/Thermal_printer#ESC/POS
 */

const encoder = new TextEncoder();

// ESC/POS Commands
const ESC = String.fromCharCode(0x1b);
const GS = String.fromCharCode(0x1d);

export const ESCPOS = {
  // Text formatting
  CENTER: `${ESC}${String.fromCharCode(97)}${String.fromCharCode(1)}`,
  LEFT: `${ESC}${String.fromCharCode(97)}${String.fromCharCode(0)}`,
  RIGHT: `${ESC}${String.fromCharCode(97)}${String.fromCharCode(2)}`,
  
  // Font size (Height, Width)
  NORMAL: `${ESC}!${String.fromCharCode(0)}`,
  BOLD: `${ESC}!${String.fromCharCode(8)}`,
  DOUBLE_HEIGHT: `${ESC}!${String.fromCharCode(16)}`,
  DOUBLE_WIDTH: `${ESC}!${String.fromCharCode(32)}`,
  BOLD_DOUBLE_HEIGHT: `${ESC}!${String.fromCharCode(24)}`,
  BOLD_DOUBLE_WIDTH: `${ESC}!${String.fromCharCode(40)}`,
  
  // Underline
  UNDERLINE_ON: `${ESC}${String.fromCharCode(45)}${String.fromCharCode(1)}`,
  UNDERLINE_OFF: `${ESC}${String.fromCharCode(45)}${String.fromCharCode(0)}`,
  
  // Line spacing
  LINE_SPACING_DEFAULT: `${ESC}${String.fromCharCode(50)}`,
  LINE_SPACING_SET: (n: number) => `${ESC}${String.fromCharCode(51)}${String.fromCharCode(n)}`,
  
  // Horizontal line
  HORIZONTAL_LINE: (width: number = 32) => "-".repeat(width),
  
  // Paper feed
  LINE_FEED: "\n",
  CUT: `${GS}V${String.fromCharCode(66)}${String.fromCharCode(0)}`,
  PARTIAL_CUT: `${GS}V${String.fromCharCode(65)}${String.fromCharCode(0)}`,
};

function formatCurrency(value: number, symbol: string): string {
  return `${symbol} ${(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPhone(phone: string): string {
  if (!phone) return "—";
  if (phone.length === 11) return `${phone.slice(0, 4)}-${phone.slice(4)}`;
  return phone;
}

function padRow(label: string, value: string, width: number = 32): string {
  const maxLabelWidth = Math.floor(width * 0.5);
  const truncatedLabel = label.length > maxLabelWidth 
    ? label.substring(0, maxLabelWidth - 1) 
    : label;
  const padding = width - truncatedLabel.length - value.length;
  if (padding < 1) return `${truncatedLabel.substring(0, width - value.length - 1)} ${value}`;
  return truncatedLabel + " ".repeat(Math.max(1, padding)) + value;
}

function centerText(text: string, width: number = 32): string {
  const trimmed = text.length > width ? text.substring(0, width) : text;
  const padding = Math.floor((width - trimmed.length) / 2);
  return " ".repeat(Math.max(0, padding)) + trimmed;
}

export async function convertReceiptToEscPos(
  data: PaymentReceiptData,
  currencySymbol?: string,
  feedLines: number = 1,
  isLaser: boolean = false
): Promise<Uint8Array> {
  currencySymbol = currencySymbol || getCurrencySymbol();

  // ─────────────────────────────
  // 48mm printers ≈ 384 dots (~32–42 chars safe)
  // ─────────────────────────────
  const WIDTH = isLaser ? 64 : 32;

  const encoder = new TextEncoder();

  // ESC/POS commands (or PCL for laser)
  const cmds = {
    init: isLaser
      ? new Uint8Array([0x1b, 0x45, 0x1b, 0x26, 0x6b, 0x32, 0x47]) // PCL Reset + CR=CR LF=CR+LF
      : new Uint8Array([0x1b, 0x40]),
    alignCenter: new Uint8Array([0x1b, 0x61, 0x01]),
    alignLeft: new Uint8Array([0x1b, 0x61, 0x00]),
    boldOn: new Uint8Array([0x1b, 0x45, 0x01]),
    boldOff: new Uint8Array([0x1b, 0x45, 0x00]),
    underlineOn: new Uint8Array([0x1b, 0x2d, 0x01]),
    underlineOff: new Uint8Array([0x1b, 0x2d, 0x00]),
    cut: isLaser
      ? new Uint8Array([0x0d, 0x0a, 0x0c, 0x1b, 0x45]) // Form Feed (ASCII 12) + PCL Reset
      : new Uint8Array([0x1d, 0x56, 0x42, 0x00]),
    lineFeed: encoder.encode(isLaser ? "\r\n" : "\n"),
  };

  const isReverted = data.is_invoice_reverted === 1;

  const text = (t: string) => encoder.encode(t + "\n");

  const center = (t: string) => {
    const trimmed = (t || "").substring(0, WIDTH);
    const pad = Math.max(0, Math.floor((WIDTH - trimmed.length) / 2));
    return encoder.encode(" ".repeat(pad) + trimmed + "\n");
  };

  const line = () => encoder.encode("-".repeat(WIDTH) + "\n");

  const money = (n: number) =>
    `${currencySymbol || getCurrencySymbol()} ${(n || 0).toLocaleString(getActiveIntlLocale(), {
      minimumFractionDigits: 2,
    })}`;

  const parts: Uint8Array[] = [];

  // ───────── INIT ─────────
  parts.push(cmds.init);

  // ───────── HEADER ─────────
  parts.push(cmds.alignCenter);
  parts.push(cmds.boldOn);
  parts.push(center((data.company_name || "—").toUpperCase()));
  parts.push(cmds.boldOff);

  parts.push(center(data.company_city || "—"));
  parts.push(center((data.connection_type || "").toUpperCase() + " SERVICE"));
  parts.push(line());

  // ───────── REVERTED ─────────
  if (isReverted) {
    parts.push(cmds.boldOn);
    parts.push(center("*** REVERTED INVOICE ***"));
    parts.push(cmds.boldOff);
    parts.push(line());
  }

  // ───────── INFO ─────────
  parts.push(cmds.alignLeft);

  const addLine = (label: string, value: string) => {
    parts.push(text(`${label}: ${value || "—"}`));
  };

  addLine("Bill No", data.bill_number);
  addLine("Status", data.status);
  addLine("Date", data.modified?.split(" ")[0] || "—");
  addLine("Customer", data.full_name);
  addLine("Phone", data.phone_number);
  if(data.connection_type =='Internet'){
    addLine("ISP", data.isp_name);
    addLine("Package", data.package_name);
  }
  if(data.mode_of_payment){
    addLine("Mode", data.mode_of_payment);
  }
  addLine("Type", data.bill_status);
  
  parts.push(line());

  // ───────── AMOUNTS ─────────
  parts.push(text(`Bill Amount: ${money(data.amount)}`));
  parts.push(text(`Discount: ${money(data.discount)}`));

  parts.push(line());

  parts.push(cmds.boldOn);
  parts.push(text(`Paid: ${money(data.paid_amount)}`));
  parts.push(cmds.boldOff);

  const balance =
    (data.amount || 0) - (data.paid_amount || 0);

  parts.push(line());

  if (balance > 0) {
    parts.push(cmds.boldOn);
    parts.push(text(`Balance: ${money(balance)}`));
    parts.push(cmds.boldOff);
  } 
  
  parts.push(line());

  // ───────── FOOTER ─────────
  parts.push(cmds.alignCenter);
  parts.push(text("Thank you for your payment"));
  parts.push(text(new Date().toLocaleString()));

  parts.push(encoder.encode("\n".repeat(feedLines)));
  parts.push(cmds.cut);

  // ───────── MERGE ─────────
  const totalLength = parts.reduce((sum, p) => sum + p.length, 0);
  const result = new Uint8Array(totalLength);

  let offset = 0;
  for (const p of parts) {
    result.set(p, offset);
    offset += p.length;
  }

  return result;
} 

export async function convertReceiptToEscPosOLD(
  data: PaymentReceiptData,
  currencySymbol?: string,
  feedLines: number = 5
): Promise<Uint8Array> {
  const WIDTH = 32;
  let output = "";

  const isReverted = data.is_invoice_reverted === 1;
  const displayStatus = isReverted ? "REVERTED" : (data.status?.toUpperCase() ?? "—");
  const issuedDate = data.modified?.split(" ")[0] ?? "—";

  // Initialize
  output += ESCPOS.NORMAL;
  output += ESCPOS.LINE_SPACING_SET(30);

  // ===== HEADER =====
  output += ESCPOS.CENTER;
  output += ESCPOS.BOLD;
  output += centerText((data.company_name || "—").toUpperCase(), WIDTH) + "\n";
  output += ESCPOS.NORMAL;
  
  // Company address (smaller)
  const address = data.company_address || "—";
  const addrLines = [];
  for (let i = 0; i < address.length; i += WIDTH) {
    addrLines.push(address.substring(i, i + WIDTH));
  }
  addrLines.forEach(line => {
    output += centerText(line, WIDTH) + "\n";
  });
  
  const connType = (data.connection_type || "—").toUpperCase();
  output += centerText(`${connType} SERVICE`, WIDTH) + "\n";
  
  output += ESCPOS.HORIZONTAL_LINE(WIDTH) + "\n\n";

  // ===== REVERTED BANNER =====
  if (isReverted) {
    output += ESCPOS.CENTER;
    output += ESCPOS.BOLD;
    output += centerText("*** PAYMENT REVERTED ***", WIDTH) + "\n";
    output += ESCPOS.NORMAL;
    output += centerText("Payment returned to customer", WIDTH) + "\n";
    output += ESCPOS.HORIZONTAL_LINE(WIDTH) + "\n\n";
  }

  // ===== BILL INFO =====
  output += ESCPOS.LEFT;
  output += ESCPOS.BOLD;
  output += padRow("Bill No:", data.bill_number || "—", WIDTH) + "\n";
  output += padRow("Status:", `[ ${displayStatus} ]`, WIDTH) + "\n";
  output += ESCPOS.NORMAL;
  output += padRow("Date:", issuedDate, WIDTH) + "\n";
  output += padRow("Due:", data.due_date || "—", WIDTH) + "\n";
  output += "\n";

  // ===== CUSTOMER DETAILS HEADER =====
  output += ESCPOS.CENTER;
  output += ESCPOS.BOLD;
  output += ESCPOS.UNDERLINE_ON;
  output += centerText("CUSTOMER DETAILS", WIDTH) + "\n";
  output += ESCPOS.UNDERLINE_OFF;
  output += ESCPOS.NORMAL;
  output += ESCPOS.LEFT;
  output += "\n";

  // ===== CUSTOMER INFO =====
  output += padRow("Name:", data.full_name || "—", WIDTH) + "\n";
  output += padRow("Phone:", formatPhone(data.phone_number), WIDTH) + "\n";
  output += padRow("Package:", data.package_name || "—", WIDTH) + "\n";
  output += padRow("Plan:", data.package_filter || "—", WIDTH) + "\n";
  const monthYear = `${data.month || "—"} ${data.year || ""}`.trim();
  output += padRow("Month:", monthYear, WIDTH) + "\n";
  output += padRow("Salesman:", data.salesman_name || "—", WIDTH) + "\n";
  output += "\n";

  // ===== PAYMENT SUMMARY HEADER =====
  output += ESCPOS.CENTER;
  output += ESCPOS.BOLD;
  output += ESCPOS.UNDERLINE_ON;
  output += centerText("PAYMENT SUMMARY", WIDTH) + "\n";
  output += ESCPOS.UNDERLINE_OFF;
  output += ESCPOS.NORMAL;
  output += ESCPOS.LEFT;
  output += "\n";

  // ===== AMOUNTS =====
  output += padRow("Bill Amount:", formatCurrency(data.amount, currencySymbol || getCurrencySymbol()), WIDTH) + "\n";
  output += padRow("Discount:", formatCurrency(data.discount, currencySymbol || getCurrencySymbol()), WIDTH) + "\n";
  output += ESCPOS.HORIZONTAL_LINE(WIDTH) + "\n";
  output += ESCPOS.BOLD;
  output += padRow("Amount Paid:", formatCurrency(data.paid_amount, currencySymbol || getCurrencySymbol()), WIDTH) + "\n";
  
  if (isReverted) {
    output += padRow(
      "Reverted:",
      `- ${formatCurrency(data.paid_amount, currencySymbol || getCurrencySymbol())}`,
      WIDTH
    ) + "\n";
  }
  
  output += ESCPOS.NORMAL;
  output += "\n";

  // ===== BALANCE =====
  const balance = (data.amount || 0) - (data.paid_amount || 0);
  if (balance > 0) {
    output += ESCPOS.BOLD;
    output += padRow("Outstanding:", formatCurrency(balance, currencySymbol || getCurrencySymbol()), WIDTH) + "\n";
    output += ESCPOS.NORMAL;
  } else if (balance === 0) {
    output += ESCPOS.CENTER;
    output += ESCPOS.BOLD;
    output += centerText("*** PAID IN FULL ***", WIDTH) + "\n";
    output += ESCPOS.NORMAL;
    output += ESCPOS.LEFT;
  }

  output += "\n";
  output += ESCPOS.CENTER;
  output += centerText("Thank You!", WIDTH) + "\n";
  output += ESCPOS.LEFT;
  output += "\n";
  output += ESCPOS.HORIZONTAL_LINE(WIDTH) + "\n";

  // ===== FOOTER =====
  output += ESCPOS.CENTER;
  output += centerText(new Date().toLocaleString(), WIDTH) + "\n";
  output += ESCPOS.LEFT;

  // Paper feed and cut
  output += "\n".repeat(feedLines);
  output += ESCPOS.CUT;

  // Convert to Uint8Array
  return encoder.encode(output);
}

export async function convertDantsuToEscPos(
  dantsu: string,
  feedLines: number = 3,
  autoCut: boolean = true,
  isLaser: boolean = false
): Promise<Uint8Array> {
  const lines = dantsu.split("\n");
  const parts: Uint8Array[] = [];
  const encoder = new TextEncoder();

  const cmds = {
    init: isLaser
      ? new Uint8Array([0x1b, 0x45, 0x1b, 0x26, 0x6b, 0x32, 0x47]) // PCL Reset + Line Termination
      : new Uint8Array([0x1b, 0x40]),
    alignCenter: new Uint8Array([0x1b, 0x61, 0x01]),
    alignLeft: new Uint8Array([0x1b, 0x61, 0x00]),
    alignRight: new Uint8Array([0x1b, 0x61, 0x02]),
    boldOn: new Uint8Array([0x1b, 0x45, 0x01]),
    boldOff: new Uint8Array([0x1b, 0x45, 0x00]),
    doubleOn: new Uint8Array([0x1b, 0x21, 0x30]), // double height and width
    doubleOff: new Uint8Array([0x1b, 0x21, 0x00]),
    cut: isLaser
      ? new Uint8Array([0x0d, 0x0a, 0x0c, 0x1b, 0x45]) // Form Feed (ASCII 12) + PCL Reset
      : new Uint8Array([0x1d, 0x56, 0x42, 0x00]),
  };

  parts.push(cmds.init);

  for (const rawLine of lines) {
    let line = rawLine.trimRight();
    if (!line) {
      parts.push(encoder.encode(isLaser ? "\r\n" : "\n"));
      continue;
    }

    // Alignment
    if (line.startsWith("[C]")) {
      parts.push(cmds.alignCenter);
      line = line.substring(3);
    } else if (line.startsWith("[R]")) {
      parts.push(cmds.alignRight);
      line = line.substring(3);
    } else if (line.startsWith("[L]")) {
      parts.push(cmds.alignLeft);
      line = line.substring(3);
    } else {
      parts.push(cmds.alignLeft);
    }

    // Bold tags: <b>...</b>
    let bold = false;
    if (line.includes("<b>")) {
      bold = true;
      line = line.replace(/<\/?b>/g, "");
    }

    // Font size tags: <font size="big">...</font>
    let big = false;
    if (line.includes('<font size="big">')) {
      big = true;
      line = line.replace(/<font size="big">|<\/font>/g, "");
    }

    if (big) {
      parts.push(cmds.doubleOn);
    }
    if (bold) {
      parts.push(cmds.boldOn);
    }

    // Thermal printers often lack Unicode glyphs — keep printable ASCII + Latin-1
    const safe = line.replace(/[^\x09\x0A\x0D\x20-\x7E]/g, "");
    parts.push(encoder.encode(safe + (isLaser ? "\r\n" : "\n")));

    if (bold) {
      parts.push(cmds.boldOff);
    }
    if (big) {
      parts.push(cmds.doubleOff);
    }
  }

  if (isLaser) {
    // Laser printers eject the page on Form Feed
    parts.push(cmds.cut);
  } else {
    if (feedLines > 0) {
      parts.push(encoder.encode("\n".repeat(feedLines)));
    }
    if (autoCut) {
      parts.push(cmds.cut);
    }
  }

  // Merge parts
  const totalLength = parts.reduce((sum, p) => sum + p.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const p of parts) {
    result.set(p, offset);
    offset += p.length;
  }

  return result;
}

/** ESC/POS cash drawer kick pulse (standard pin 2). */
export function getCashDrawerKickBytes(): Uint8Array {
  return new Uint8Array([0x1b, 0x70, 0x00, 0x19, 0xfa]);
}

export async function openCashDrawer(): Promise<boolean> {
  try {
    const settings = usePrinterStore.getState().settings;
    if (!isThermalPrinterConfigured(settings)) {
      console.warn('[drawer] No printer connection — drawer kick skipped');
      return false;
    }
    await printEscPosBytes(getCashDrawerKickBytes(), { allowPicker: false, settings });
    return true;
  } catch (e) {
    console.warn('[drawer] kick failed', e);
    return false;
  }
}

